import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Custom coupon UI for cart-summary.liquid.
 * Mirrors cart-discount.js but uses the custom input/apply controls.
 */
class CartCouponComponent extends HTMLElement {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {boolean} */
  #processing = false;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.#activeFetch?.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('#cart_discount_apply')) {
      event.preventDefault();
      this.#applyDiscount();
      return;
    }

    const removeButton = event.target.closest('.cart_discount_remove');
    if (removeButton instanceof HTMLElement) {
      event.preventDefault();
      const code = removeButton.dataset.discountCode;
      if (code) this.#removeDiscount(code);
    }
  };

  /**
   * @returns {string | undefined}
   */
  #getSectionId() {
    if (typeof this.dataset.sectionId === 'string' && this.dataset.sectionId) {
      return this.dataset.sectionId;
    }

    const cartItems = this.closest('cart-items-component');
    if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
      return cartItems.dataset.sectionId;
    }

    const drawerCartItems = document.querySelector('cart-items-component[data-drawer]');
    if (drawerCartItems instanceof HTMLElement && drawerCartItems.dataset.sectionId) {
      return drawerCartItems.dataset.sectionId;
    }

    const pageCartItems = document.querySelector('cart-items-component');
    if (pageCartItems instanceof HTMLElement && pageCartItems.dataset.sectionId) {
      return pageCartItems.dataset.sectionId;
    }

    return undefined;
  }

  /**
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];

    this.querySelectorAll('.cart_discount_remove[data-discount-code]').forEach((button) => {
      if (button instanceof HTMLElement && typeof button.dataset.discountCode === 'string') {
        discountCodes.push(button.dataset.discountCode);
      }
    });

    return discountCodes;
  }

  async #applyDiscount() {
    if (this.#processing) return;

    const input = this.querySelector('#cart_discount_input');
    const applyButton = this.querySelector('#cart_discount_apply');

    if (!(input instanceof HTMLInputElement) || !(applyButton instanceof HTMLButtonElement)) return;

    const discountCodeValue = input.value.trim();

    if (!discountCodeValue) {
      this.#showMessage('Enter discount code', 'error');
      return;
    }

    const sectionId = this.#getSectionId();
    if (!sectionId) {
      this.#showMessage('Something went wrong', 'error');
      return;
    }

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(discountCodeValue)) {
      this.#showMessage('Discount code already applied', 'error');
      return;
    }

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    this.#setLoading(true);

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes?.find(
          (/** @type {{ code: string; applicable: boolean }} */ discount) =>
            discount.code === discountCodeValue && discount.applicable === false
        )
      ) {
        this.#showMessage('Invalid or not applicable code', 'error');
        return;
      }

      const sectionHtml = data.sections?.[sectionId];
      if (!sectionHtml) {
        this.#showMessage('Something went wrong', 'error');
        return;
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, 'cart-coupon-component'));
      morphSection(sectionId, sectionHtml);
      this.#showMessage(`Discount applied: ${discountCodeValue}`, 'success');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#showMessage('Something went wrong', 'error');
    } finally {
      this.#activeFetch = null;
      this.#setLoading(false);
    }
  }

  /**
   * @param {string} discountCode
   */
  async #removeDiscount(discountCode) {
    if (this.#processing) return;

    const sectionId = this.#getSectionId();
    if (!sectionId) {
      this.#showMessage('Something went wrong', 'error');
      return;
    }

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    this.#setLoading(true);

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      const sectionHtml = data.sections?.[sectionId];

      if (!sectionHtml) {
        this.#showMessage('Failed to remove discount', 'error');
        return;
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, 'cart-coupon-component'));
      morphSection(sectionId, sectionHtml);
      this.#showMessage('Discount removed', 'success');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#showMessage('Failed to remove discount', 'error');
    } finally {
      this.#activeFetch = null;
      this.#setLoading(false);
    }
  }

  /**
   * @param {string} text
   * @param {'success' | 'error'} type
   */
  #showMessage(text, type) {
    const message = document.getElementById('cart_discount_message');
    if (!(message instanceof HTMLElement)) return;

    message.textContent = text;
    message.classList.remove('cart_discount_message--success', 'cart_discount_message--error');
    message.classList.add(`cart_discount_message--${type}`);
    message.classList.remove('hidden');

    window.clearTimeout(this.#messageTimeout);
    this.#messageTimeout = window.setTimeout(() => {
      message.classList.add('hidden');
    }, 3000);
  }

  /** @type {number | undefined} */
  #messageTimeout;

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    this.#processing = isLoading;

    const applyButton = this.querySelector('#cart_discount_apply');
    const spinner = this.querySelector('.cart_discount_apply__spinner');

    if (applyButton instanceof HTMLButtonElement) {
      applyButton.disabled = isLoading;
      applyButton.classList.toggle('is-loading', isLoading);
    }

    if (spinner instanceof HTMLElement) {
      spinner.classList.toggle('hidden', !isLoading);
    }
  }
}

if (!customElements.get('cart-coupon-component')) {
  customElements.define('cart-coupon-component', CartCouponComponent);
}
